import api from '../lib/api'
import type { LoginForm, RegisterForm, User, ApiResponse } from '../types'

export const authService = {
  async login(credentials: LoginForm): Promise<{ user: User; token: string; refreshToken: string }> {
    const response = await api.post('/auth/login', credentials)
    const data = response.data.data || response.data
    return {
      user: data.user,
      token: data.token || data.access_token,
      refreshToken: data.refreshToken || data.refresh_token,
    }
  },

  async register(userData: RegisterForm): Promise<{ user: User; token: string; refreshToken: string }> {
    const response = await api.post('/auth/register', userData)
    const data = response.data.data || response.data
    return {
      user: data.user,
      token: data.token || data.access_token,
      refreshToken: data.refreshToken || data.refresh_token,
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      // Even if logout fails, clear local storage
    } finally {
      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
    }
  },

  async refreshToken(): Promise<{ token: string; refreshToken: string }> {
    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');
    const response = await api.post<ApiResponse<{ token: string; refreshToken: string }>>('/auth/refresh', { refreshToken })
    return response.data.data
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me')
    return response.data
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email })
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password })
  },

  // Helper functions
  setToken(token: string): void {
    localStorage.setItem('authToken', token)
  },

  getToken(): string | null {
    return localStorage.getItem('authToken')
  },

  setRefreshToken(refreshToken: string): void {
    localStorage.setItem('refreshToken', refreshToken)
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  },

  clearTokens(): void {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
  },

  isAuthenticated(): boolean {
    return !!this.getToken()
  }
} 